import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import EmployeeFormDialog from '@/components/employees/EmployeeFormDialog';
import {
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Mail,
  Building2,
  CheckCircle2,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { sendEmployeeInvitation } from '@/components/email/sendEmail';
import { generateCarrierPacketPDF, downloadPDF } from '@/components/utils/pdfGeneration';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

// Matches server/src/services/employeeSyncService.ts's placeholder domain for
// census rows synced without a real email (e.g. Proposal Mode uploads).
const isPendingPlaceholderEmail = (email) => Boolean(email) && email.endsWith('@no-email.pending.internal');

export default function EmployeeManagement() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployer, setSelectedEmployer] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [inviteCodeToShow, setInviteCodeToShow] = useState(null);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employeesData, employersData] = await Promise.all([
        api.entities.Employee.list(),
        api.entities.Employer.list()
      ]);
      setEmployees(employeesData);
      setEmployers(employersData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployerName = (employerId) => {
    const employer = employers.find(e => e.id === employerId);
    return employer?.company_name || 'Unknown';
  };

  const ensureEnrollmentToken = async (employee) => {
    if (employee.enrollment_token) return employee.enrollment_token;
    const newToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    await api.entities.Employee.update(employee.id, { enrollment_token: newToken });
    setEmployees((prev) => prev.map((e) => e.id === employee.id ? { ...e, enrollment_token: newToken } : e));
    return newToken;
  };

  const copyInviteLink = async (employee) => {
    if (employee.enrollment_token_claimed || employee.profile_confirmed) {
      toast.info('This employee already activated their account.');
      return;
    }

    try {
      const token = await ensureEnrollmentToken(employee);
      const inviteUrl = `${globalThis.location.origin}/InviteCodeEntry?token=${token}`;
      if (globalThis.navigator?.clipboard?.writeText) {
        await globalThis.navigator.clipboard.writeText(inviteUrl);
        toast.success('Invite link copied to clipboard');
      } else {
        // Fallback: show modal/toast with link
        setInviteCodeToShow({
          token,
          email: employee.user_email,
          name: `${employee.first_name} ${employee.last_name}`,
          url: inviteUrl
        });
        toast.success('Invite link ready to share');
      }
    } catch (err) {
      console.error('Failed to copy invite link', err);
      toast.error('Could not copy invite link');
    }
  };

  const getEnrollmentStatusClass = (status) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
    if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEmployer = selectedEmployer === 'all' || employee.employer_id === selectedEmployer;
    return matchesSearch && matchesEmployer && employee.is_active !== false;
  });

  // Group employees under their respective company so each employer's
  // employees are shown together, matching the companies listed in Employers.
  const employeesByEmployer = (() => {
    const groups = new Map();
    filteredEmployees.forEach((employee) => {
      const key = employee.employer_id || '__unassigned__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(employee);
    });

    return Array.from(groups.entries())
      .map(([employerId, groupEmployees]) => ({
        employerId,
        employerName: employerId === '__unassigned__' ? 'Unassigned' : getEmployerName(employerId),
        employees: groupEmployees,
      }))
      .sort((a, b) => a.employerName.localeCompare(b.employerName));
  })();

  const handleFormSaved = (result) => {
    setShowAddModal(false);
    setEditingEmployee(null);
    if (result?.mode === 'create' && result.inviteInfo) {
      setInviteCodeToShow(result.inviteInfo);
    }
    loadData();
  };

  const deactivateEmployee = async (employee) => {
    try {
      await api.entities.Employee.update(employee.id, {
        is_active: false,
        deleted_at: new Date().toISOString(),
        deleted_by: (await api.auth.me()).email
      });
      
      await api.entities.AuditLog.create({
        entity_type: 'Employee',
        entity_id: employee.id,
        action: 'delete',
        performed_by: (await api.auth.me()).email,
        notes: `Deactivated employee ${employee.first_name} ${employee.last_name}`
      });
      
      toast.success('Employee deactivated');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to deactivate employee');
    }
  };

  const confirmDeactivateEmployee = (employee) => {
    openConfirm({
      title: 'Deactivate this employee?',
      description: `${employee.first_name} ${employee.last_name} will lose access and move to inactive records. Historical data remains available for reporting.`,
      confirmLabel: 'Deactivate employee',
      isDestructive: true,
      onConfirm: () => deactivateEmployee(employee)
    });
  };

  const sendInviteEmail = async (employee) => {
    if (isPendingPlaceholderEmail(employee.user_email)) {
      toast.error('Add a real email for this employee before sending an invite');
      return;
    }
    try {
      // Generate new token if doesn't exist
      if (!employee.enrollment_token) {
        const newToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        await api.entities.Employee.update(employee.id, {
          enrollment_token: newToken
        });
        employee.enrollment_token = newToken;
      }

      const employer = employers.find(e => e.id === employee.employer_id);
      const inviteUrl = `${globalThis.location.origin}/InviteCodeEntry`;
      
      const emailData = sendEmployeeInvitation(employee.user_email, {
        employeeName: `${employee.first_name} ${employee.last_name}`,
        employerName: employer?.company_name || 'Your Company',
        enrollmentUrl: inviteUrl,
        inviteCode: employee.enrollment_token,
        enrollmentDeadline: employee.enrollment_window_end ? format(new Date(employee.enrollment_window_end), 'MMMM d, yyyy') : null,
        hrEmail: employer?.hr_contact_email
      });

      await api.integrations.Core.SendEmail({
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.html
      });

      await api.entities.Employee.update(employee.id, {
        invitation_sent_date: new Date().toISOString()
      });
      
      toast.success('Invitation sent with enrollment code!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to send invite email');
    }
  };

  const downloadCarrierPacket = async (employee) => {
    try {
      const [enrollments, products, familyMembers, employerData] = await Promise.all([
        api.entities.Enrollment.filter({ employee_id: employee.id, status: 'active' }),
        api.entities.BenefitProduct.list(),
        api.entities.FamilyMember.filter({ employee_id: employee.id, is_active: true }),
        api.entities.Employer.filter({ id: employee.employer_id })
      ]);

      const pdf = await generateCarrierPacketPDF(employee, enrollments, products, familyMembers, employerData[0]);
      downloadPDF(pdf, `carrier_packet_${employee.first_name}_${employee.last_name}.pdf`);
      toast.success('Carrier packet downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate carrier packet');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Employee Management</h1>
          <p className="text-slate-500 mt-1">
            Manage employee profiles and enrollment status
          </p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
              <SelectTrigger className="w-full md:w-64">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by Employer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employers</SelectItem>
                {employers.map(employer => (
                  <SelectItem key={employer.id} value={employer.id}>
                    {employer.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Employer</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enrollment</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                employeesByEmployer.map((group) => (
                  <React.Fragment key={group.employerId}>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableCell colSpan={6} className="py-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <span className="font-semibold text-slate-700">{group.employerName}</span>
                          <Badge variant="outline" className="ml-1">{group.employees.length}</Badge>
                        </div>
                      </TableCell>
                    </TableRow>
                    {group.employees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    onClick={() => navigate(`/EmployeeDetail/${employee.id}`)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3 w-fit">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-medium flex-shrink-0">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {employee.first_name} {employee.last_name}
                          </p>
                          {!isPendingPlaceholderEmail(employee.user_email) && (
                            <p className="text-sm text-slate-500">{employee.user_email}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getEmployerName(employee.employer_id)}</TableCell>
                    <TableCell>{employee.job_title || '-'}</TableCell>
                    <TableCell>
                      {employee.profile_confirmed ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Confirmed
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getEnrollmentStatusClass(employee.enrollment_status)}>
                        {employee.enrollment_status || 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingEmployee(employee)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendInviteEmail(employee)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Invite
                          </DropdownMenuItem>
                          {(!employee.enrollment_token_claimed && !employee.profile_confirmed) && (
                            <DropdownMenuItem onClick={() => copyInviteLink(employee)}>
                              <Mail className="w-4 h-4 mr-2" />
                              Copy Invite Link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => downloadCarrierPacket(employee)}>
                            <Download className="w-4 h-4 mr-2" />
                            Carrier Packet
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => confirmDeactivateEmployee(employee)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                    ))}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <EmployeeFormDialog
        open={showAddModal || !!editingEmployee}
        onOpenChange={(next) => {
          if (!next) {
            setShowAddModal(false);
            setEditingEmployee(null);
          }
        }}
        employee={editingEmployee}
        employers={employers}
        onSaved={handleFormSaved}
      />

      {/* Invite Code Display Modal */}
      <Dialog open={!!inviteCodeToShow} onOpenChange={() => setInviteCodeToShow(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invitation Created Successfully!</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="text-center">
              <p className="text-slate-600 mb-4">
                Share this invitation code with <strong>{inviteCodeToShow?.name}</strong>
              </p>
              
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 space-y-3">
                <p className="text-sm text-slate-600">Invitation Code:</p>
                <p className="text-3xl font-bold text-emerald-600 tracking-wider font-mono">
                  {inviteCodeToShow?.token}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteCodeToShow?.token);
                    toast.success('Code copied to clipboard!');
                  }}
                  className="mt-2"
                >
                  Copy Code
                </Button>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700">Instructions for {inviteCodeToShow?.name}:</p>
              <ol className="text-sm text-slate-600 space-y-1 ml-4 list-decimal">
                <li>Visit: <code className="bg-white px-2 py-1 rounded text-xs">{inviteCodeToShow?.url}</code></li>
                <li>Enter the invitation code shown above</li>
                <li>Complete their profile and enroll in benefits</li>
              </ol>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  const text = `Hi ${inviteCodeToShow?.name},\n\nYou've been invited to enroll in your benefits!\n\nInvitation Code: ${inviteCodeToShow?.token}\n\nVisit: ${inviteCodeToShow?.url}\n\nEnter your code and complete your enrollment.`;
                  navigator.clipboard.writeText(text);
                  toast.success('Full instructions copied!');
                }}
              >
                Copy Full Message
              </Button>
              <Button onClick={() => {
                setInviteCodeToShow(null);
                loadData();
              }}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}